import { db } from "services/firebase";
import { buildBaseUserDoc } from "components/auth/buildBaseUserDoc";
import { isMorningReportDomain } from "./morningReportPaths";

/**
 * ⚠️ A MORNINGREPORT SIGNUP IS A LEARNER, AND THAT IS DECIDED AT SIGNUP, NOT AFTER.
 *
 * The role used to live only in a localStorage hint, read by /onboarding after the
 * user verified their email. That hint is per browser and per domain, so it is gone
 * whenever the verify link opens somewhere else: a phone mail app, another device,
 * or the primary domain (the verify link always opens there, and Firebase drops the
 * morningreport.si continueUrl when the domain isn't authorized). Those learners
 * landed on the account-type chooser. So the learner profile is now written the
 * moment the account is created, and the hint is only a fallback.
 */

const readSignupRoleHint = () => {
  try {
    return localStorage.getItem("pendingSignupRole");
  } catch {
    return null;
  }
};

/**
 * Whether this signup is a MorningReport learner. Invites and share links carry
 * their own role and always win; an explicit ?role= other than learner wins too.
 * Otherwise morningreport.si, ?role=learner or the landing page's hint mean learner.
 */
export const isLearnerSignup = ({
  urlRole = null,
  hasInvite = false,
  hasShareCode = false,
} = {}) => {
  if (hasInvite || hasShareCode) return false;
  if (urlRole) return urlRole === "learner";
  if (isMorningReportDomain()) return true;
  return readSignupRoleHint() === "learner";
};

/**
 * Create the learner profile for a just-created account, unless one exists. Never
 * overwrites: `role` is immutable after create (firestore.rules), so a profile that
 * is already there is left exactly as it is. Never throws, so it can't block a
 * signup: if it fails, /onboarding still defaults the role to learner.
 */
export const ensureLearnerProfile = async (user) => {
  if (!user) return;
  try {
    const ref = db.collection("users").doc(user.uid);
    await db.runTransaction(async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists) return;
      tx.set(ref, buildBaseUserDoc({ user, email: user.email, role: "learner" }));
    });
  } catch (err) {
    console.error("Could not create the learner profile at signup:", err);
  }
};
